import type { RawAttribute } from '@manudev/model';
import type { BuildStore } from '../types';
import { outerFootprint } from './footprint';
import { nativeLength, siteBox, type Point2 } from './frame';
import { orderedStoreys, sourceStorey, storeyHeight } from './storeys';
import type { BuildParams, BuildResult } from './types';

const RISER = 0.175;
const GOING = 0.28;
const WIDTH = 1;
const MARGIN = 0.5;

// ifc-lite has no stair builder, so this one is hand-emitted: a sawtooth profile swept across
// the width, chained to the storey placement the same way the store chains a new storey.
export async function addStair(store: BuildStore, params: BuildParams): Promise<BuildResult> {
  const storeys = await orderedStoreys(store);
  const storey = await sourceStorey(store, params.storey);
  const height = params.height ?? storeyHeight(storeys, storey);
  const width = params.width ?? WIDTH;
  // equal risers adding up to the storey height, rather than a last step out of the floor
  const risers = Math.max(2, Math.ceil(height / RISER));
  const riser = height / risers;
  const going = params.thickness ?? GOING;
  const run = (risers - 1) * going;

  const footprint = outerFootprint(await store.wallSegments(storey.expressID));
  const seat = await where(store, footprint, width);
  const anchor = await store.anchor(storey.expressID);
  const owner = anchor.ownerHistoryId != null ? `#${anchor.ownerHistoryId}` : null;
  // every coordinate leaves as a STEP REAL: these slots are IfcLengthMeasure, not integers
  const metres = (v: number): RawAttribute => ({ real: nativeLength(anchor, v) });
  const raw = (type: string, attributes: RawAttribute[]) =>
    store.addRaw(type, attributes, { product: false });

  const points: number[] = [];
  for (const [u, v] of profile(risers, riser, going, run))
    points.push(await raw('IfcCartesianPoint', [[metres(u), metres(v)]]));
  const outline = await raw('IfcPolyline', [points.map((id) => `#${id}`)]);
  const shape = await raw('IfcArbitraryClosedProfileDef', ['.AREA.', 'Stair', `#${outline}`]);

  // local X runs up the flight and local Z sweeps across it, so local Y comes out as world up
  const origin = await raw('IfcCartesianPoint', [[metres(0), metres(0), metres(0)]]);
  const across = await raw('IfcDirection', [[{ real: seat.dir[1] }, { real: -seat.dir[0] }, 0]]);
  const along = await raw('IfcDirection', [[{ real: seat.dir[0] }, { real: seat.dir[1] }, 0]]);
  const frame = await raw('IfcAxis2Placement3D', [`#${origin}`, `#${across}`, `#${along}`]);
  const up = await raw('IfcDirection', [[0, 0, { real: 1 }]]);
  const solid = await raw('IfcExtrudedAreaSolid', [
    `#${shape}`,
    `#${frame}`,
    `#${up}`,
    metres(width),
  ]);
  const body = await raw('IfcShapeRepresentation', [
    `#${anchor.bodyContextId}`,
    'Body',
    'SweptSolid',
    [`#${solid}`],
  ]);
  const product = await raw('IfcProductDefinitionShape', [null, null, [`#${body}`]]);

  const at = await raw('IfcCartesianPoint', [[metres(seat.at[0]), metres(seat.at[1]), metres(0)]]);
  const local = await raw('IfcAxis2Placement3D', [`#${at}`, null, null]);
  const place = await raw('IfcLocalPlacement', [`#${anchor.storeyPlacementId}`, `#${local}`]);

  const stairId = await store.addRaw('IfcStair', [
    await store.guid(),
    owner,
    params.name ?? `${storey.name} stair`,
    null,
    null,
    `#${place}`,
    `#${product}`,
    null,
    '.STRAIGHT_RUN_STAIR.',
  ]);
  await raw('IfcRelContainedInSpatialStructure', [
    await store.guid(),
    owner,
    null,
    null,
    [`#${stairId}`],
    `#${anchor.storeyId}`,
  ]);

  return {
    created: [stairId],
    removed: [],
    note: `${risers} risers of ${riser.toFixed(3)} m over ${run.toFixed(2)} m on ${storey.name}, ${height} m floor to floor`,
  };
}

// Closed sawtooth in the run/height plane: up the steps, then straight back down to the floor
// at the run end. A solid under the flight instead of a waist keeps the ring simple.
function profile(risers: number, riser: number, going: number, run: number): Point2[] {
  const points: Point2[] = [[0, 0]];
  let u = 0;
  for (let i = 1; i <= risers; i++) {
    points.push([u, i * riser]);
    if (i < risers) {
      u += going;
      points.push([u, i * riser]);
    }
  }
  points.push([run, 0]);
  points.push([0, 0]);
  return points;
}

interface Seat {
  // storey-local XY of the first riser's outer bottom corner, and the direction it climbs in
  at: Point2;
  dir: Point2;
}

// Over the stair the model already has when there is one, otherwise in a corner of the
// footprint, running along its longer side.
async function where(store: BuildStore, footprint: Point2[], width: number): Promise<Seat> {
  const elements = await store.elements();
  const flight = elements.find((e) => e.type.toUpperCase() === 'IFCSTAIRFLIGHT');
  const box = flight ? siteBox(await store.mesh([flight.expressID])) : null;
  if (box) return seat([box.min[0], -box.max[2]], [box.max[0], -box.min[2]], width);
  const xs = footprint.map((p) => p[0]);
  const ys = footprint.map((p) => p[1]);
  return seat(
    [Math.min(...xs) + MARGIN, Math.min(...ys) + MARGIN],
    [Math.max(...xs) - MARGIN, Math.max(...ys) - MARGIN],
    width,
  );
}

function seat(lo: Point2, hi: Point2, width: number): Seat {
  const alongY = hi[1] - lo[1] >= hi[0] - lo[0];
  const dir: Point2 = alongY ? [0, 1] : [1, 0];
  const sweep: Point2 = [dir[1], -dir[0]];
  const start: Point2 = alongY ? [(lo[0] + hi[0]) / 2, lo[1]] : [lo[0], (lo[1] + hi[1]) / 2];
  return { at: [start[0] - (sweep[0] * width) / 2, start[1] - (sweep[1] * width) / 2], dir };
}
